"use client"

import { SERVER_URL } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { Quote, Star, ArrowLeft, ArrowRight, Video, Play } from 'lucide-react';
import VideoModal from '../VideoModal';

const testimonials = [
    {
        name: "Exclusive Highlights",
        role: "Samuel Chimwendo, Managing Director",
        video: "/SNC.mp4",
        textLists: ["SNC AI"]
    }
];

export function PremiumTestimonials({ items }: { items?: any[] }) {
    const displayItems = items && items.length > 0 ? items : testimonials;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = setInterval(() => {
            setDirection(1);
            setCurrentIndex((prev) => (prev + 1) % displayItems.length);
        }, 8000);

        return () => clearInterval(timer);
    }, [displayItems.length]);

    const slideVariants = {
        enter: (direction: number) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0,
            scale: 0.8,
            rotateY: direction > 0 ? 45 : -45
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1,
            rotateY: 0
        },
        exit: (direction: number) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0,
            scale: 0.8,
            rotateY: direction < 0 ? 45 : -45
        })
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.23, 0.86, 0.39, 0.96]
            }
        }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3
            }
        }
    };

    const nextTestimonial = () => {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % displayItems.length);
    };

    const prevTestimonial = () => {
        setDirection(-1);
        setCurrentIndex((prev) => (prev - 1 + displayItems.length) % displayItems.length);
    };

    return (
        <div className="relative py-20 bg-white dark:bg-zinc text-black dark:text-white overflow-hidden rounded-3xl border border-black/5 dark:border-white/5 transition-colors duration-500">
            {/* Enhanced Background Effects */}
            <div className="absolute inset-0">
                <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.08] via-blue-400/[0.05] to-rose-500/[0.08]"
                    animate={{
                        backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
                    }}
                    transition={{
                        duration: 25,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    style={{
                        backgroundSize: '400% 400%'
                    }}
                />

                <motion.div
                    className="absolute top-1/3 left-1/4 w-72 h-72 bg-blue-400/15 rounded-full blur-3xl text-blue-400"
                    animate={{
                        x: [0, 150, 0],
                        y: [0, 80, 0],
                        scale: [1, 1.2, 1],
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <motion.div
                    className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-rose-400/15 rounded-full blur-3xl"
                    animate={{
                        x: [0, -100, 0],
                        y: [0, -60, 0],
                        scale: [1, 1.3, 1],
                    }}
                    transition={{
                        duration: 22,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />

                {[...Array(12)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-white/30 rounded-full"
                        style={{
                            left: `${15 + (i * 7)}%`,
                            top: `${25 + (i * 5)}%`,
                        }}
                        animate={{
                            y: [0, -50, 0],
                            opacity: [0.2, 1, 0.2],
                            scale: [1, 2, 1],
                        }}
                        transition={{
                            duration: 3 + i * 0.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: i * 0.3,
                        }}
                    />
                ))}
            </div>

            <motion.div
                ref={containerRef}
                className="relative z-10 max-w-7xl mx-auto px-6"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
            >
                {/* Main Testimonial Display */}
                <div className="relative max-w-5xl mx-auto mb-16">
                    <div className="relative h-[650px] md:h-[450px] perspective-1000">
                        <AnimatePresence initial={false} custom={direction}>
                            <motion.div
                                key={currentIndex}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{
                                    x: { type: "spring", stiffness: 300, damping: 30 },
                                    opacity: { duration: 0.4 },
                                    scale: { duration: 0.4 },
                                    rotateY: { duration: 0.6 }
                                }}
                                className="absolute inset-0"
                            >
                                <div className="relative h-full bg-black/5 dark:bg-black/40 backdrop-blur-xl rounded-3xl border border-black/10 dark:border-white/[0.1] p-8 md:p-12 overflow-hidden group shadow-2xl transition-colors duration-500">
                                    {/* Quote icon */}
                                    <motion.div
                                        className="absolute top-8 right-8 opacity-10 dark:opacity-20"
                                        animate={{ rotate: [0, 10, 0] }}
                                        transition={{ duration: 4, repeat: Infinity }}
                                    >
                                        <Quote className="w-16 h-16 text-black dark:text-white" />
                                    </motion.div>

                                    <div className="relative z-10 h-full flex flex-col items-center justify-center">
                                        {/* Video Content */}
                                        <div className="w-full h-full relative rounded-2xl overflow-hidden bg-black/20 group-hover:bg-black/10 transition-colors">
                                            {displayItems[currentIndex].video ? (
                                                <video
                                                    key={displayItems[currentIndex].video}
                                                    src={displayItems[currentIndex].video.startsWith('http') || displayItems[currentIndex].video === '/snc.mp4' || displayItems[currentIndex].video === '/SNC.mp4'
                                                        ? displayItems[currentIndex].video
                                                        : `${SERVER_URL}${displayItems[currentIndex].video.startsWith('/') ? '' : '/'}${displayItems[currentIndex].video}`
                                                    }
                                                    className="w-full h-full object-cover"
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-black/20 dark:text-white/20">
                                                    <Video className="w-20 h-20" />
                                                </div>
                                            )}

                                            {/* Play Button Overlay */}
                                            {displayItems[currentIndex].video && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <motion.button
                                                        onClick={() => {
                                                            const videoUrl = displayItems[currentIndex].video.startsWith('http') || displayItems[currentIndex].video === '/snc.mp4' || displayItems[currentIndex].video === '/SNC.mp4'
                                                                ? displayItems[currentIndex].video
                                                                : `${SERVER_URL}${displayItems[currentIndex].video.startsWith('/') ? '' : '/'}${displayItems[currentIndex].video}`;
                                                            setSelectedVideo(videoUrl);
                                                        }}
                                                        className="p-6 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white hover:scale-110 transition-transform"
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                    >
                                                        <Play className="w-12 h-12 fill-current" />
                                                    </motion.button>
                                                </div>
                                            )}

                                            {/* Overlay Text */}
                                            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.5 }}
                                                >
                                                    <div className="flex flex-wrap gap-2 mb-4">
                                                        {(displayItems[currentIndex].textLists || ["Exclusive Highlights"]).map((text, i) => (
                                                            <span key={i} className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-sm font-medium border border-white/10">
                                                                {text}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <h3 className="text-2xl font-bold mb-1">
                                                        {displayItems[currentIndex].name || "SNC AI Innovation"}
                                                    </h3>
                                                    <p className="text-white/60 text-sm">
                                                        {displayItems[currentIndex].role || "Experience the future of AI"}
                                                    </p>
                                                </motion.div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="flex justify-center items-center gap-6 mt-8">
                        <motion.button
                            onClick={prevTestimonial}
                            className="p-3 rounded-full bg-black/5 dark:bg-white/[0.08] border border-black/10 dark:border-white/[0.15] backdrop-blur-sm text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/[0.15] transition-all"
                            whileHover={{ scale: 1.1, backgroundColor: "rgba(0, 0, 0, 0.1)" }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </motion.button>

                        <div className="flex gap-3">
                            {displayItems.map((_, index) => (
                                <motion.button
                                    key={index}
                                    onClick={() => {
                                        setDirection(index > currentIndex ? 1 : -1);
                                        setCurrentIndex(index);
                                    }}
                                    className={`w-3 h-3 rounded-full transition-all ${index === currentIndex
                                        ? 'bg-blue-600 dark:bg-blue-400 scale-125'
                                        : 'bg-black/20 dark:bg-white/30 hover:bg-black/40 dark:hover:bg-white/50'
                                        }`}
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                />
                            ))}
                        </div>

                        <motion.button
                            onClick={nextTestimonial}
                            className="p-3 rounded-full bg-black/5 dark:bg-white/[0.08] border border-black/10 dark:border-white/[0.15] backdrop-blur-sm text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/[0.15] transition-all"
                            whileHover={{ scale: 1.1, backgroundColor: "rgba(0, 0, 0, 0.1)" }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <ArrowRight className="w-5 h-5" />
                        </motion.button>
                    </div>
                </div>

                {/* SNC AI Training Section */}
                <motion.div
                    className="flex justify-center text-center mt-12"
                    variants={fadeInUp}
                >
                    <p className="max-w-4xl text-lg md:text-xl font-medium leading-relaxed bg-gradient-to-r from-blue-400 via-blue-300 to-rose-400 bg-clip-text text-transparent">
                        SNC AI Training offers a comprehensive, futuristic learning ecosystem designed to master autonomous agents and advanced AI ecosystems through practical, industry-leading modular solutions.
                    </p>
                </motion.div>
            </motion.div>

            <VideoModal
                isOpen={!!selectedVideo}
                onClose={() => setSelectedVideo(null)}
                videoSrc={selectedVideo || ''}
            />
        </div>
    );
}
